import {ChartOptions} from 'chart.js';

import {alpha} from '@mui/material/styles';

import {greenColors, orangeColors, actions} from '../../../../../layouts/theme/designSystem';
import type {WorkforceDashboardAlignmentShapeScores} from '../../types/workforceDashboardTypes';

export const ALIGNMENT_SHAPE_DIMENSION_KEYS = [
  'coreCompetencies',
  'hardSkills',
  'tools',
  'domainKnowledge',
  'softSkills',
  'responsibilities',
] as const;

export type AlignmentShapeDimensionKey = (typeof ALIGNMENT_SHAPE_DIMENSION_KEYS)[number];

export const ALIGNMENT_SHAPE_MAX_ROLE_OVERLAYS = 2;

const ALIGNMENT_SHAPE_FILL_OPACITY = 0.25;

export const alignmentShapeDatasetStyles = [
  {
    borderColor: greenColors[500],
    backgroundColor: alpha(greenColors[500], ALIGNMENT_SHAPE_FILL_OPACITY),
    pointBackgroundColor: greenColors[500],
  },
  {
    borderColor: orangeColors[900],
    backgroundColor: alpha(orangeColors[900], ALIGNMENT_SHAPE_FILL_OPACITY),
    pointBackgroundColor: orangeColors[900],
  },
  {
    borderColor: actions.primary,
    backgroundColor: alpha(actions.primary, ALIGNMENT_SHAPE_FILL_OPACITY),
    pointBackgroundColor: actions.primary,
  },
] as const;

export const mapAlignmentShapeScoresToChartValues = (scores: WorkforceDashboardAlignmentShapeScores): number[] =>
  ALIGNMENT_SHAPE_DIMENSION_KEYS.map((key) => scores[key] ?? 0);

export const hasAlignmentShapeScores = (scores: WorkforceDashboardAlignmentShapeScores | undefined): boolean =>
  !!scores && ALIGNMENT_SHAPE_DIMENSION_KEYS.some((key) => scores[key] !== null);

export const buildAlignmentShapeChartOptions = (
  dimensionLabels: string[],
  formatTooltipValue: (dimensionKey: AlignmentShapeDimensionKey, value: number, datasetIndex: number) => string
): ChartOptions<'radar'> => ({
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        boxWidth: 12,
        boxHeight: 12,
        padding: 16,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const dimensionKey = ALIGNMENT_SHAPE_DIMENSION_KEYS[context.dataIndex];
          const label = dimensionLabels[context.dataIndex] ?? '';
          return `${label}: ${formatTooltipValue(dimensionKey, context.parsed.r, context.datasetIndex)}`;
        },
      },
    },
  },
  scales: {
    r: {
      min: 0,
      max: 100,
      ticks: {
        stepSize: 20,
        backdropColor: 'transparent',
      },
      pointLabels: {
        font: {
          size: 12,
        },
      },
    },
  },
});
